import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { AlertTriangle, X } from 'lucide-react'

export interface ConfirmOptions {
  title?: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  // 'danger' (default) paints the confirm button red — these are destructive
  // actions. 'primary' for a neutral "are you sure you want to proceed".
  tone?: 'danger' | 'primary'
}

interface ConfirmDialogProps extends ConfirmOptions {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
}

/**
 * In-app confirmation dialog — the replacement for `window.confirm`.
 *
 * A native `confirm()` blocks the whole renderer, can't be styled or themed,
 * and in an embedded/automated browser it wedges the page until someone
 * dismisses it by hand. This is a plain React modal instead: same look as the
 * rest of the app, Escape/backdrop to cancel, focus on the confirm button.
 */
export default function ConfirmDialog({
  open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  tone = 'danger', onCancel, onConfirm,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    // Focus the confirm button so Enter accepts and Escape cancels, matching
    // the keyboard behaviour people expect from the native dialog.
    confirmRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCancel()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [open, onCancel])

  if (!open) return null

  return (
    <div
      className="overlay"
      role="alertdialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {tone === 'danger' && (
              <AlertTriangle size={17} color="var(--color-danger, #ef4444)" />
            )}
            {title || 'Are you sure?'}
          </h3>
          <button className="modal-close" onClick={onCancel} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div style={{
          fontSize: '0.9375rem',
          color: 'var(--color-text-secondary)',
          marginBottom: '1.25rem',
          lineHeight: 1.7,
        }}>
          {message}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            className={tone === 'danger' ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * `const { confirm, confirmDialog } = useConfirm()` — then
 * `if (await confirm({ message: '…' })) { … }` and render `{confirmDialog}`.
 *
 * Keeps call sites as terse as the `window.confirm` they replace, so a promise
 * is resolved by whichever button the user presses (false on cancel/Escape/
 * backdrop).
 */
export function useConfirm() {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null)
  const resolveRef = useRef<((ok: boolean) => void) | null>(null)

  const settle = useCallback((ok: boolean) => {
    setOpts(null)
    const resolve = resolveRef.current
    resolveRef.current = null
    resolve?.(ok)
  }, [])

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    // A second confirm while one is open would strand the first promise —
    // resolve it as cancelled rather than leaking an un-awaited caller.
    resolveRef.current?.(false)
    setOpts(options)
    return new Promise<boolean>((resolve) => { resolveRef.current = resolve })
  }, [])

  const confirmDialog = (
    <ConfirmDialog
      {...(opts || { message: '' })}
      open={opts !== null}
      onCancel={() => settle(false)}
      onConfirm={() => settle(true)}
    />
  )

  return { confirm, confirmDialog }
}
